import {
  ActivityCategoriesIdsMap,
  ActivityWithCategoryAndLocationsAndReportedDetails,
} from '@wilson/interfaces';

export type SimplifiedShiftBreakOption = 30 | 60 | 'exact' | null;

export type SimpleShiftActivity =
  ActivityWithCategoryAndLocationsAndReportedDetails;

export type ExactBreakValidationError =
  | 'incomplete'
  | 'invalid_range'
  | 'outside_shift'
  | null;

export function sortActivitiesForSimpleShift(
  activities: SimpleShiftActivity[],
): SimpleShiftActivity[] {
  return [...activities].sort(
    (left, right) =>
      new Date(left.startDatetime).getTime() -
      new Date(right.startDatetime).getTime(),
  );
}

export function isSimpleShiftActivities(
  activities: SimpleShiftActivity[] | null | undefined,
): boolean {
  if (!activities?.length) {
    return false;
  }

  const sorted = sortActivitiesForSimpleShift(activities);

  if (sorted.length === 1) {
    return true;
  }

  if (sorted.length !== 3) {
    return false;
  }

  const [first, middle, last] = sorted;
  const isMiddleBreak =
    middle.activityCategoryId === ActivityCategoriesIdsMap.Break;
  const hasMatchingTask =
    !!first.activityCategoryId &&
    first.activityCategoryId === last.activityCategoryId;

  if (!isMiddleBreak || !hasMatchingTask) {
    return false;
  }

  const locationIds = [
    first.startLocationId,
    first.endLocationId,
    middle.startLocationId,
    middle.endLocationId,
    last.startLocationId,
    last.endLocationId,
  ];

  const firstLocationId = locationIds[0];
  return (
    !!firstLocationId &&
    locationIds.every((locationId) => locationId === firstLocationId)
  );
}

export function getShiftDurationMinutes(
  startTime: Date,
  endTime: Date,
): number {
  const startMs = startTime.getTime();
  let endMs = endTime.getTime();
  if (endMs <= startMs) {
    endMs += 24 * 60 * 60 * 1000;
  }
  return (endMs - startMs) / (1000 * 60);
}

export function isBreakDurationOptionAvailable(
  shiftDurationMinutes: number,
  breakMinutes: 30 | 60,
): boolean {
  return shiftDurationMinutes > breakMinutes;
}

export function validateExactBreakWithinShift(params: {
  shiftStart: Date;
  shiftEnd: Date;
  breakStart: Date | null;
  breakEnd: Date | null;
}): ExactBreakValidationError {
  const { shiftStart, shiftEnd, breakStart, breakEnd } = params;
  if (!breakStart || !breakEnd) {
    return 'incomplete';
  }

  const shiftDurationMinutes = getShiftDurationMinutes(shiftStart, shiftEnd);
  const resolvedShiftEnd = new Date(
    shiftStart.getTime() + shiftDurationMinutes * 60 * 1000,
  );

  let resolvedBreakStart = applyTimeOfDay(shiftStart, breakStart);
  let resolvedBreakEnd = applyTimeOfDay(shiftStart, breakEnd);

  if (resolvedBreakStart.getTime() < shiftStart.getTime()) {
    resolvedBreakStart = new Date(
      resolvedBreakStart.getTime() + 24 * 60 * 60 * 1000,
    );
  }
  if (resolvedBreakEnd.getTime() <= resolvedBreakStart.getTime()) {
    resolvedBreakEnd = new Date(
      resolvedBreakEnd.getTime() + 24 * 60 * 60 * 1000,
    );
  }

  if (resolvedBreakEnd.getTime() <= resolvedBreakStart.getTime()) {
    return 'invalid_range';
  }

  if (
    resolvedBreakStart.getTime() <= shiftStart.getTime() ||
    resolvedBreakEnd.getTime() >= resolvedShiftEnd.getTime()
  ) {
    return 'outside_shift';
  }

  return null;
}

export function getBreakOptionFromActivities(
  activities: SimpleShiftActivity[],
): {
  breakOption: SimplifiedShiftBreakOption;
  breakStartTime: Date | null;
  breakEndTime: Date | null;
} {
  const sorted = sortActivitiesForSimpleShift(activities);
  if (
    sorted.length !== 3 ||
    sorted[1].activityCategoryId !== ActivityCategoriesIdsMap.Break
  ) {
    return {
      breakOption: null,
      breakStartTime: null,
      breakEndTime: null,
    };
  }

  const breakStartTime = new Date(sorted[1].startDatetime);
  const breakEndTime = new Date(sorted[1].endDatetime);
  const durationMinutes = Math.round(
    (breakEndTime.getTime() - breakStartTime.getTime()) / (1000 * 60),
  );

  if (durationMinutes === 30) {
    return {
      breakOption: 30,
      breakStartTime: null,
      breakEndTime: null,
    };
  }

  if (durationMinutes === 60) {
    return {
      breakOption: 60,
      breakStartTime: null,
      breakEndTime: null,
    };
  }

  return {
    breakOption: 'exact',
    breakStartTime,
    breakEndTime,
  };
}

export function getAgreementIdFromActivities(
  activities: SimpleShiftActivity[],
): {
  agreementId: string | null;
  hasMixedAgreements: boolean;
} {
  if (!activities.length) {
    return { agreementId: null, hasMixedAgreements: false };
  }

  const agreementIds = activities.map(
    (activity) => activity.agreementId ?? null,
  );
  const firstAgreementId = agreementIds[0];
  const hasMixedAgreements = agreementIds.some(
    (agreementId) => agreementId !== firstAgreementId,
  );

  return {
    agreementId: hasMixedAgreements ? null : firstAgreementId,
    hasMixedAgreements,
  };
}

function applyTimeOfDay(baseDate: Date, timeSource: Date): Date {
  const result = new Date(baseDate);
  result.setHours(
    timeSource.getHours(),
    timeSource.getMinutes(),
    timeSource.getSeconds(),
    0,
  );
  return result;
}
